import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccount, useConfig, useSwitchChain, useWriteContract } from 'wagmi'
import { getPublicClient } from '@wagmi/core'
import { keccak256, toBytes } from 'viem'
import { operatorAbi } from '@/abi/operatorAbi'
import { getContractAddress } from '@/config/contracts'
import { postPlayerAdjustLifetimeEur, type AdjustLifetimeEurRequest } from '@/lib/api'
import { chainQueryKeys } from './_chainQueryKeys'

const OPERATOR_ROLE = keccak256(toBytes('OPERATOR_ROLE'))

export interface SetLifetimeEurAdjustmentVars {
    chainId: number
    playerAddress: `0x${string}`
    enteredDeltaEurMinor: bigint
    wonDeltaEurMinor: bigint
    ethUsd8: bigint
    eurUsd8: bigint
    atBlock: bigint
    idempotencyKey: `0x${string}`
    reason: string
}

export interface SetLifetimeEurAdjustmentResult {
    txHash: `0x${string}`
}

export class OperatorRoleMissingError extends Error {
    readonly account: `0x${string}`

    constructor(account: `0x${string}`) {
        super(`Connected wallet ${account} does not hold OPERATOR_ROLE on operator`)
        this.name = 'OperatorRoleMissingError'
        this.account = account
    }
}

// Wraps `BlockpotOperator.setLifetimeEurAdjustment` against the operator's connected
// wallet. The console's only direct on-chain write — every other MLRO write
// is service-relayed via chain-write jobs. We do this one client-side because
// the setter is a manual, low-frequency MLRO correction and the audit trail
// lands via a follow-up `/v1/players/:address/adjust-lifetime-eur` call that
// records the confirmed tx hash + metadata.
//
// Role check is best-effort: we read `hasRole(OPERATOR_ROLE, account)` from
// operator right before sending the tx and throw `OperatorRoleMissingError` so the
// dialog can surface a helpful message instead of letting the wallet bounce
// with a raw revert. The on-chain `onlyRole` modifier is the authoritative
// check.
export function useSetLifetimeEurAdjustment() {
    const queryClient = useQueryClient()
    const config = useConfig()
    const { address: account, chainId: walletChainId } = useAccount()
    const { switchChainAsync } = useSwitchChain()
    const { writeContractAsync } = useWriteContract()

    return useMutation<SetLifetimeEurAdjustmentResult, Error, SetLifetimeEurAdjustmentVars>({
        mutationFn: async (vars) => {
            if (!account) {
                throw new Error('Connect a wallet before submitting an EUR adjustment')
            }
            const operator = getContractAddress(vars.chainId, 'operator')
            if (!operator) {
                throw new Error(`operator contract not configured for chain ${vars.chainId}`)
            }
            // The wallet must sign on the target chain; viem refuses a
            // mismatched wallet chain, so prompt the switch first.
            if (walletChainId !== vars.chainId) {
                await switchChainAsync({ chainId: vars.chainId })
            }
            const client = getPublicClient(config, { chainId: vars.chainId })
            if (!client) {
                throw new Error('No public client available for the requested chain')
            }

            const hasRole = (await client.readContract({
                address: operator,
                abi: operatorAbi,
                functionName: 'hasRole',
                args: [OPERATOR_ROLE, account],
            })) as boolean
            if (!hasRole) {
                throw new OperatorRoleMissingError(account)
            }

            const txHash = await writeContractAsync({
                chainId: vars.chainId,
                address: operator,
                abi: operatorAbi,
                functionName: 'setLifetimeEurAdjustment',
                args: [
                    vars.playerAddress,
                    vars.enteredDeltaEurMinor,
                    vars.wonDeltaEurMinor,
                    vars.ethUsd8,
                    vars.eurUsd8,
                    vars.atBlock,
                    vars.idempotencyKey,
                ],
            })

            await client.waitForTransactionReceipt({ hash: txHash })

            // Audit-log record. The tx has already landed, so a failure here
            // surfaces as a mutation error the operator can retry off the
            // on-chain event; the endpoint always exists.
            const body: AdjustLifetimeEurRequest = {
                chainId: vars.chainId,
                enteredDeltaEurMinor: vars.enteredDeltaEurMinor.toString(),
                wonDeltaEurMinor: vars.wonDeltaEurMinor.toString(),
                ethUsd8: vars.ethUsd8.toString(),
                eurUsd8: vars.eurUsd8.toString(),
                atBlock: vars.atBlock.toString(),
                idempotencyKey: vars.idempotencyKey,
                txHash,
                reason: vars.reason,
            }
            await postPlayerAdjustLifetimeEur(vars.playerAddress, body)
            return { txHash }
        },
        onSuccess: (_res, vars) => {
            queryClient.invalidateQueries({
                queryKey: chainQueryKeys.operatorLifetime(vars.chainId, vars.playerAddress),
            })
            queryClient.invalidateQueries({
                queryKey: ['audit-log-player-recent', vars.playerAddress.toLowerCase()],
            })
            queryClient.invalidateQueries({ queryKey: ['audit-log-list'] })
            queryClient.invalidateQueries({
                queryKey: ['player-detail', vars.playerAddress.toLowerCase()],
            })
        },
    })
}

// Generates a deterministic-by-input but unique-per-call idempotency key for
// the on-chain `_appliedAdjustment` mapping. Same player + same submission
// instant + same nonce ⇒ same hash, so a network retry of the same submission
// hits the contract's idempotency early-return rather than double-applying.
export function generateIdempotencyKey(playerAddress: `0x${string}`): `0x${string}` {
    const nonce = new Uint8Array(32)
    crypto.getRandomValues(nonce)
    const nowMs = BigInt(Date.now())
    const playerBytes = toBytes(playerAddress)
    const nowBytes = toBytes(`0x${nowMs.toString(16).padStart(16, '0')}`)
    const buf = new Uint8Array(playerBytes.length + nowBytes.length + nonce.length)
    buf.set(playerBytes, 0)
    buf.set(nowBytes, playerBytes.length)
    buf.set(nonce, playerBytes.length + nowBytes.length)
    return keccak256(buf)
}
